import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../utils/supabaseClient';
import Modal from '../../components/Modal';

function highlightMatch(text, searchTerm) {
  if (!searchTerm) return text;
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? <mark key={i}>{part}</mark> : part
  );
}

const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

function List() {
  const [contentList, setContentList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [planName, setPlanName] = useState(null);

  const searchInputRef = useRef(null);

  const handleDelete = async () => {
    if (!itemToDelete) return;

    const { error } = await supabase
      .from('generated_content')
      .delete()
      .eq('id', itemToDelete.id);

    if (error) {
      alert('Error deleting content.');
    } else {
      const updatedList = contentList.filter((item) => item.id !== itemToDelete.id);
      setContentList(updatedList);
      setFilteredList(updatedList);
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
    const fetchContentAndPlan = async () => {
      setLoading(true);

      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;

      if (!user) {
        setLoading(false);
        return;
      }

      const { data: account, error: accountError } = await supabase
        .from('accounts')
        .select('plan')
        .eq('id', user.id)
        .single();

      if (accountError) {
        console.error('Error fetching account:', accountError);
      }

      setPlanName(account?.plan === 'pro' ? 'Pro' : 'Free');

      let data = null;
      let error = null;

      // Try FK query
      ({ data, error } = await supabase
        .from('generated_content')
        .select(`
          id,
          title,
          content_type,
          created_at,
          profile(id, name)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false }));

      // If 400 or FK error: fallback
      if (error) {
        if (
          error.code === 'PGRST200' ||
          error.message?.includes('relationship') ||
          error.message?.includes('foreign key')
        ) {
          ({ data, error } = await supabase
            .from('generated_content')
            .select(`
              id,
              title,
              content_type,
              created_at,
              profile
            `)
            .eq('user_id', user.id)
            .order('created_at', { ascending: false }));
        }
      }

      if (error) {
        console.error('Error fetching content:', error);
      }

      setContentList(data || []);
      setFilteredList(data || []);
      setLoading(false);
    };

    fetchContentAndPlan();
  }, []);


  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredList(contentList);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredList(
        contentList.filter(
          (item) =>
            item.title?.toLowerCase().includes(term) ||
            item.content_type?.toLowerCase().includes(term) ||
            item.profile?.name?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, contentList]);

  if (loading) {
    return (
      <div className="centered-loading">
        <p>Loading content...</p>
      </div>
    );
  }

  return (
    <>
      <header>
        <div className="section-title">
          <h1>Content</h1>
        </div>
        <div className="breadcrumbs">
          <p>Content <i className="bi bi-chevron-right"></i> View List</p>
        </div>
      </header>

      <section className='scroll'>
        <div className="contain">

          {contentList.length > 0 && (
            <>
              <div className="view-search">
                <input
                  type="text"
                  id="view-search"
                  ref={searchInputRef}
                  placeholder="Search content"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  className="btn-unset"
                  type="button"
                  onClick={() => searchInputRef.current?.focus()}
                >
                  <i className="bi bi-search"></i>
                </button>
              </div>
              {searchTerm.trim() && filteredList.length === 0 && (
                <p className="no-results-message">
                  No results found for "<strong>{searchTerm}</strong>"
                </p>
              )}
            </>
          )}

          <div className="view-rows">
            {filteredList.length === 0 && !searchTerm.trim() ? (
              <div className="no-content centered-loading">
                <button
                  type="button"
                  className="btn-outlined"
                  onClick={() => window.location.href = '/create'}
                >
                  Create your first content
                </button>
              </div>
            ) : (
              filteredList.map((item) => (
                <div className="view-row" key={item.id}>
                  <div>
                    <h2>{highlightMatch(item.title, searchTerm)}</h2>
                    <p>
                      <span>Type: {highlightMatch(capitalize(item.content_type), searchTerm)}</span>{' '}
                      <span>
                        Profile:{' '}
                        {item.profile?.name
                          ? highlightMatch(item.profile.name, searchTerm)
                          : 'None'}
                      </span>{' '}
                      <span>Created: {new Date(item.created_at).toLocaleString()}</span>
                    </p>
                  </div>
                  <div className='view-row-actions'>
                    {planName === 'Pro' && (
                      <button
                        type="button"
                        className="btn-outlined btn-delete"
                        onClick={() => {
                          setItemToDelete(item);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </button>
                    )}
                    <a href={`/content/${item.id}`} className='btn-outlined'>View</a>
                  </div>
                </div>
              ))
            )}
          </div>

        </div>
      </section>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Delete"
        actions={[
          {
            label: 'Delete',
            className: 'btn-outlined btn-delete',
            onClick: handleDelete,
          },
          {
            label: 'Cancel',
            className: 'btn-outlined',
            onClick: () => setShowDeleteModal(false),
          },
        ]}
      >
        <p>Are you sure you want to delete <strong>{itemToDelete?.title}</strong>? This action cannot be undone.</p>
      </Modal>
    </>
  );
}

export default List;
